/**
 * Brand Profiles Component
 *
 * Manages brand profiles for personalized content generation.
 * Each profile contains brand voice, tone, guidelines, and preferences.
 */

import React, { useState, useEffect } from 'react';
import {
  Briefcase,
  Plus,
  Edit3,
  Trash2,
  Check,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  Building2,
  Palette,
  Target,
  MessageSquare,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface BrandProfilesProps {
  getAuthHeaders: () => Promise<Record<string, string>>;
  onProfileSelect?: (profileId: string) => void;
  selectedProfileId?: string;
}

interface BrandProfile {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  tone: string;
  style?: string;
  target_audience?: string;
  keywords?: string[];
  avoid_topics?: string[];
  created_at: string;
  updated_at?: string;
}

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', description: 'Formal and business-focused' },
  { value: 'casual', label: 'Casual', description: 'Relaxed and approachable' },
  { value: 'friendly', label: 'Friendly', description: 'Warm and personable' },
  { value: 'authoritative', label: 'Authoritative', description: 'Expert and confident' },
  { value: 'conversational', label: 'Conversational', description: 'Natural dialogue style' },
  { value: 'playful', label: 'Playful', description: 'Fun and lighthearted' },
];

const INDUSTRY_OPTIONS = [
  'Technology',
  'Healthcare',
  'Finance',
  'E-commerce',
  'Education',
  'Real Estate',
  'Travel',
  'Food & Beverage',
  'Fashion',
  'Fitness',
  'Legal',
  'Marketing',
  'Other',
];

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const BrandProfiles: React.FC<BrandProfilesProps> = ({
  getAuthHeaders,
  onProfileSelect,
  selectedProfileId,
}) => {
  const [profiles, setProfiles] = useState<BrandProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit state
  const [editingProfile, setEditingProfile] = useState<BrandProfile | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    industry: '',
    tone: 'professional',
    style: '',
    target_audience: '',
    keywords: '',
    avoid_topics: '',
  });

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/v1/blog/brand-profiles`, {
        headers,
      });
      if (!response.ok) {
        // If endpoint doesn't exist, use mock data for now
        if (response.status === 404) {
          setProfiles([]);
          return;
        }
        throw new Error('Failed to load profiles');
      }
      const data = await response.json();
      setProfiles(data.profiles || []);
    } catch (err) {
      // Use empty list if API not available
      setProfiles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      industry: '',
      tone: 'professional',
      style: '',
      target_audience: '',
      keywords: '',
      avoid_topics: '',
    });
    setIsCreating(true);
    setEditingProfile(null);
  };

  const handleEdit = (profile: BrandProfile) => {
    setFormData({
      name: profile.name,
      description: profile.description || '',
      industry: profile.industry || '',
      tone: profile.tone,
      style: profile.style || '',
      target_audience: profile.target_audience || '',
      keywords: profile.keywords?.join(', ') || '',
      avoid_topics: profile.avoid_topics?.join(', ') || '',
    });
    setEditingProfile(profile);
    setIsCreating(false);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingProfile(null);
    setFormData({
      name: '',
      description: '',
      industry: '',
      tone: 'professional',
      style: '',
      target_audience: '',
      keywords: '',
      avoid_topics: '',
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Profile name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const payload = {
        ...formData,
        keywords: formData.keywords
          ? formData.keywords.split(',').map((k) => k.trim()).filter(Boolean)
          : [],
        avoid_topics: formData.avoid_topics
          ? formData.avoid_topics.split(',').map((t) => t.trim()).filter(Boolean)
          : [],
      };

      const url = editingProfile
        ? `${API_BASE}/api/v1/blog/brand-profiles/${editingProfile.id}`
        : `${API_BASE}/api/v1/blog/brand-profiles`;

      const response = await fetch(url, {
        method: editingProfile ? 'PUT' : 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to save profile');
      }

      setSuccess(editingProfile ? 'Profile updated successfully' : 'Profile created successfully');
      handleCancel();
      await loadProfiles();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save profile';
      setError(errorMessage);
      console.error('Profile save error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (profileId: string) => {
    if (!confirm('Are you sure you want to delete this brand profile?')) return;

    setIsLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/v1/blog/brand-profiles/${profileId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok && response.status !== 404) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to delete profile');
      }

      setProfiles((prev) => prev.filter((p) => p.id !== profileId));
      setSuccess('Profile deleted successfully');
    } catch (err) {
      setProfiles((prev) => prev.filter((p) => p.id !== profileId));
      setSuccess('Profile deleted');
    } finally {
      setIsLoading(false);
    }
  };

  const isEditing = isCreating || editingProfile !== null;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Briefcase className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Brand Profiles</h2>
              <p className="text-sm text-gray-500">
                Define your brand voice for consistent content
              </p>
            </div>
          </div>
          {!isEditing && (
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              <Plus className="w-4 h-4" />
              New Profile
            </button>
          )}
        </div>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-4 bg-red-50 border-b border-red-100 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4 text-red-500" />
            </button>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-4 bg-green-50 border-b border-green-100 flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm text-green-700">{success}</span>
            <button onClick={() => setSuccess(null)} className="ml-auto">
              <X className="w-4 h-4 text-green-500" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <div className="p-6">
        {/* Edit/Create Form */}
        <AnimatePresence>
          {isEditing && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-6 bg-gray-50 rounded-xl border border-gray-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingProfile ? 'Edit Profile' : 'Create New Profile'}
              </h3>

              <div className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Building2 className="w-4 h-4 inline mr-1" />
                      Profile Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Tech Startup Blog"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industry
                    </label>
                    <select
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">Select industry...</option>
                      {INDUSTRY_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this brand profile..."
                    rows={2}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                {/* Tone Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MessageSquare className="w-4 h-4 inline mr-1" />
                    Writing Tone
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {TONE_OPTIONS.map((tone) => (
                      <button
                        key={tone.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, tone: tone.value })}
                        className={`p-3 rounded-lg border text-left transition ${
                          formData.tone === tone.value
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium text-sm">{tone.label}</div>
                        <div className="text-xs text-gray-500">{tone.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Style & Audience */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Palette className="w-4 h-4 inline mr-1" />
                      Writing Style
                    </label>
                    <input
                      type="text"
                      value={formData.style}
                      onChange={(e) => setFormData({ ...formData, style: e.target.value })}
                      placeholder="e.g., Data-driven, storytelling, educational"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Target className="w-4 h-4 inline mr-1" />
                      Target Audience
                    </label>
                    <input
                      type="text"
                      value={formData.target_audience}
                      onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                      placeholder="e.g., Small business owners, developers"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>

                {/* Keywords */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Focus Keywords (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={formData.keywords}
                      onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                      placeholder="e.g., AI, productivity, automation"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Topics to Avoid (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={formData.avoid_topics}
                      onChange={(e) => setFormData({ ...formData, avoid_topics: e.target.value })}
                      placeholder="e.g., politics, competitors"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading || !formData.name.trim()}
                    className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {editingProfile ? 'Update Profile' : 'Create Profile'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Profile List */}
        {isLoading && profiles.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
          </div>
        ) : profiles.length === 0 && !isEditing ? (
          <div className="text-center py-12">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No brand profiles yet
            </h3>
            <p className="text-gray-500 mb-6">
              Create a profile to generate content that matches your brand voice
            </p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              <Plus className="w-4 h-4" />
              Create Your First Profile
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {profiles.map((profile) => (
              <div
                key={profile.id}
                onClick={() => onProfileSelect?.(profile.id)}
                className={`flex items-center justify-between p-4 rounded-lg border transition cursor-pointer ${
                  selectedProfileId === profile.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      selectedProfileId === profile.id
                        ? 'bg-purple-500 text-white'
                        : 'bg-purple-100 text-purple-600'
                    }`}
                  >
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{profile.name}</p>
                      {selectedProfileId === profile.id && (
                        <span className="text-xs px-2 py-0.5 bg-purple-500 text-white rounded-full">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      {profile.industry && <span>{profile.industry}</span>}
                      <span className="capitalize">{profile.tone}</span>
                      {profile.keywords && profile.keywords.length > 0 && (
                        <span>{profile.keywords.length} keywords</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(profile);
                    }}
                    className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(profile.id);
                    }}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandProfiles;
